import {
    BNPL_GATEWAY_TYPES,
    X_PAY_GATEWAY_TYPES,
    BNPL_NORMALIZED_GATEWAYS,
    X_PAY_NORMALIZED_GATEWAYS,
    normalizeGatewayName,
} from '@app/dashboard/constants/payment-method-types';

export interface IPaymentMethodObject {
    gateway: string;
    name: string;
    active: boolean;
    rank: number;
    group_rank: number;
}

export interface IPaymentMethodsByCategory {
    methods: IPaymentMethodObject[];
    bnpl: IPaymentMethodObject[];
    x_pay: IPaymentMethodObject[];
}

/**
 * Checks if a gateway type is an X Pay method
 * @param gatewayType - The original gateway type (lowercase)
 * @param normalizedGateway - The normalized gateway name
 * @returns True if it's an X Pay method
 */
export const isXPayMethod = (
    gatewayType: string,
    normalizedGateway: string
): boolean => {
    const gatewayTypeLower = gatewayType.toLowerCase();
    return (
        (X_PAY_GATEWAY_TYPES as readonly string[]).includes(gatewayTypeLower) ||
        (X_PAY_NORMALIZED_GATEWAYS as readonly string[]).includes(normalizedGateway as any)
    );
};

/**
 * Checks if a gateway type is a BNPL method
 * @param gatewayType - The original gateway type (lowercase)
 * @param normalizedGateway - The normalized gateway name
 * @returns True if it's a BNPL method
 */
export const isBnplMethod = (
    gatewayType: string,
    normalizedGateway: string
): boolean => {
    const gatewayTypeLower = gatewayType.toLowerCase();
    return (
        (BNPL_GATEWAY_TYPES as readonly string[]).includes(gatewayTypeLower) ||
        (BNPL_NORMALIZED_GATEWAYS as readonly string[]).includes(normalizedGateway as any)
    );
};

/**
 * Creates a payment method object from gateway type and integration ID
 * @param gatewayType - The gateway type from integration
 * @param integrationId - The integration ID
 * @returns Payment method object
 */
export const createPaymentMethodObject = (
    gatewayType: string,
    integrationId: number
): IPaymentMethodObject => {
    const gatewayTypeLower = gatewayType.toLowerCase();
    const normalizedGateway = normalizeGatewayName(gatewayType);
    const methodName = gatewayTypeLower.replace(/_/g, '-');

    return {
        gateway: normalizedGateway,
        name: methodName,
        active: true,
        rank: integrationId,
        group_rank: integrationId,
    };
};

/**
 * Categorizes a payment method object into methods, bnpl, or x_pay
 * @param paymentMethod - The payment method object
 * @param gatewayType - The original gateway type
 * @param normalizedGateway - The normalized gateway name
 * @returns The category name: 'methods', 'bnpl', or 'x_pay'
 */
export const categorizePaymentMethod = (
    paymentMethod: IPaymentMethodObject,
    gatewayType: string,
    normalizedGateway: string
): 'methods' | 'bnpl' | 'x_pay' => {
    if (isXPayMethod(gatewayType, normalizedGateway)) {
        return 'x_pay';
    }
    if (isBnplMethod(gatewayType, normalizedGateway)) {
        return 'bnpl';
    }
    return 'methods';
};
